import {createSelector} from '@reduxjs/toolkit'
import getNumberOfNights from 'components/data/URLParams/mappers/getNumberOfNights'
import {toggle} from 'opticks'
import {anyPass, find, last, pathOr, propEq} from 'ramda'
import {RootState} from 'store'

import {defaultCountry} from '@daedalus/core/src/localization/business/countries'
import {getIsSplitBookingEligibleByPrice} from '@daedalus/core/src/offer/business/offersMapping'
import {
  HotelFeesBreakdown,
  Offer,
  OfferPrice
} from '@daedalus/core/src/offer/types/offer'
import {getTotalPrice} from '@daedalus/core/src/price/business/price'
import {Room} from '@daedalus/core/src/room/types/room'
import {searchApi} from '@daedalus/core/src/sapi/services/searchApi'
import {getUrlParams} from '@daedalus/core/src/utils/url'
import {UrlCheckoutParamsType} from '@daedalus/core/src/utils/url/types/UrlParams'

import {getCheckoutUrlFromOffer} from '../../business/offer'
import {OVERLAY_IDS} from '../../modules/overlay/selectors'
import {roomsFetchDone, roomsFetchError} from './slice'

const MIN_DIFFERENCE_PERCENTAGE = 0.015

const pathToRoomsList = ['rooms', 'roomsList']
const pathToRoomsRtk = ['data', 'rooms']

type Selector<S> = (state: RootState) => S

export const getRooms: Selector<Room[]> = createSelector(
  (state: RootState) => {
    const newSapiEndpoints = toggle(
      '32ab3efe-new-sapi-endpoints-v2',
      false,
      true
    )

    // @ts-expect-error RTK Query serializes the request params and store in the Redux module
    const getRoomsState = searchApi.endpoints.getRooms.select()(state)

    // We want to firstly check cached data from RSP (RAA) and if it doesn't exist fallback to SAPI data.
    // Because sometimes SAPI data can not include current offer
    // Remove this logic after RSP clean up
    const roomList = pathOr<Room[]>([], pathToRoomsList, state)
    const roomRTK = pathOr<Room[]>([], pathToRoomsRtk, getRoomsState)
    const availableRooms = roomList.length > 0 ? roomList : roomRTK

    return newSapiEndpoints
      ? availableRooms
      : pathOr<Room[]>([], pathToRoomsList, state)
  },
  rooms => rooms
)

export const getCheapestRoom = createSelector(
  getRooms,
  rooms => rooms[0] || null
)

export const getCheapestOffer = createSelector(
  getCheapestRoom,
  cheapestRoom => cheapestRoom?.offers[0] || null
)

export const getFlatOffersFromRooms = createSelector(getRooms, rooms => {
  return rooms.reduce<Offer[]>((acc, room) => {
    return [...acc, ...room.offers]
  }, [])
})

export const getSplitBookingOfferId = createSelector(
  [getUrlParams],
  ({oid, sboid}) => (oid === sboid ? oid : null)
)
export const selectGetNumberOfNights = createSelector(
  [getUrlParams],
  params => {
    const {checkIn, checkOut} = params as UrlCheckoutParamsType
    return getNumberOfNights({checkIn, checkOut})
  }
)
export const getMatchingSplitBookingOffer = createSelector(
  [
    selectGetNumberOfNights,
    getSplitBookingOfferId,
    getRooms,
    getFlatOffersFromRooms
  ],
  (numberOfNights, sboid, rooms, flatOffers) => {
    if (numberOfNights < 2 || !sboid) return null

    const room = rooms.find(room => room.hasClickedOffer)

    const splitBookingOffer = room?.offers?.find(offer => offer.isClicked)
    if (!splitBookingOffer) return null
    if (flatOffers.length === 1) return splitBookingOffer
    const sortedOffers = flatOffers.toSorted(
      (a, b) => getTotalPrice(a.prices[0]) - getTotalPrice(b.prices[0])
    )
    if (!sortedOffers[0]?.isClicked) return null
    const nextCheapestOffer =
      sortedOffers[
        sortedOffers.findIndex(offer => offer.id === splitBookingOffer?.id) + 1
      ]
    if (!nextCheapestOffer) return null

    const sbPrice = getTotalPrice(splitBookingOffer.prices[0])
    const nextOfferPrice = getTotalPrice(nextCheapestOffer.prices[0])

    const isEligibleByPrice = getIsSplitBookingEligibleByPrice(
      sbPrice,
      nextOfferPrice,
      MIN_DIFFERENCE_PERCENTAGE
    )

    return isEligibleByPrice ? splitBookingOffer : null
  }
)
export const nightlyDisplayTotalPath = ['chargeable', 'nightlyDisplayTotal']

export const basePath = ['chargeable', 'base']
export const nightlyBasePath = ['chargeable', 'nightlyBase']

export const taxesPath = ['chargeable', 'taxes']
export const nightlyTaxesPath = ['chargeable', 'nightlyTaxes']

/* TODO: remove total paths after BoFH remove these properties from the api */
export const nightlyTotalPath = ['chargeable', 'nightlyTotal']

export const hotelFeesPath = ['hotelFees', 'total']

export const getHotelFees = pathOr(0, hotelFeesPath)

export const getHotelNightlyFees = (price?: OfferPrice) =>
  price?.hotelFees?.nightly ?? 0

export const hotelFeesNightlyPath = ['hotelFees', 'nightly']

export const hotelFeesBreakdownPath = ['hotelFees', 'breakdown']

export const getHotelFeesBreakdown = pathOr<HotelFeesBreakdown>(
  [],
  hotelFeesBreakdownPath
)

export const getRoomById = (roomId: string | unknown) => (state: RootState) => {
  const rooms = getRooms(state)
  return find<Room>(propEq('id', roomId))(rooms)
}

export const getModuleState = (state: RootState) => {
  const newSapiEndpoints = toggle('32ab3efe-new-sapi-endpoints-v2', false, true)

  // @ts-expect-error RTK Query serializes the request params and store in the Redux module
  const getRoomsModule = searchApi.endpoints.getRooms.select()(state)

  // We want to firstly check cached data from RSP (RAA) and if it doesn't exist fallback to SAPI data.
  // Because sometimes SAPI data can not include current offer
  // Remove this logic after RSP clean up
  const bSideModuleState =
    state?.rooms?.fetchRoomsState || getRoomsModule?.status

  return newSapiEndpoints ? bSideModuleState : state?.rooms?.fetchRoomsState
}

export const getOfferById =
  (roomId?: string, offerId?: string) => (state: RootState) => {
    const room = getRoomById(roomId)(state)
    if (!room) return null

    const offers = pathOr<Offer[]>([], ['offers'], room)
    return find<Offer>(propEq('id', offerId))(offers)
  }

export const getRoomDetails = (state: RootState) => {
  const topmostOverlay = last(state.overlay)
  if (
    !topmostOverlay ||
    !propEq('id', OVERLAY_IDS.roomDetails, topmostOverlay)
  ) {
    return
  }

  return getRoomById(topmostOverlay.extraState?.roomId)(state)
}

export const getSelectedRoomPosition = (state: RootState) => {
  return state.rooms?.selectedRoomPosition
}

export const getSelectedOfferPosition = (state: RootState) => {
  return state.rooms?.selectedOfferPosition
}

export const getSelectedOfferCheckoutUrl = (
  state: RootState,
  roomId?: string,
  offerId?: string
) => {
  const storedUrl = state?.rooms?.selectedOfferCheckoutUrl
  if (storedUrl) return storedUrl

  const offer = getOfferById(roomId, offerId)(state)
  const checkoutLink = getCheckoutUrlFromOffer(offer)
  return checkoutLink?.href
}

export const getAreRoomsLoaded = (state: RootState) => {
  const newSapiEndpoints = toggle('32ab3efe-new-sapi-endpoints-v2', false, true)
  // @ts-expect-error RTK Query serializes the request params and store in the Redux module
  const getRoomsModule = searchApi.endpoints.getRooms.select()(state)

  const areNewEndpointRoomsLoaded =
    (getRoomsModule?.isSuccess && !getRoomsModule?.isUninitialized) ||
    getRoomsModule?.isError

  const areOldEndpointRoomsLoaded = anyPass([
    propEq('fetchRoomsState', roomsFetchDone.type),
    propEq('fetchRoomsState', roomsFetchError.type)
  ])(state.rooms)

  // We want to firstly check cached data from RSP (RAA) and if it doesn't exist fallback to SAPI data.
  // Because sometimes SAPI data can not include current offer
  // Remove this logic after RSP clean up
  const availableRoomsLoaded =
    areOldEndpointRoomsLoaded || areNewEndpointRoomsLoaded

  return newSapiEndpoints ? availableRoomsLoaded : areOldEndpointRoomsLoaded
}

export const getTaxDisplayLogic = createSelector(
  (state: RootState) => state.rooms,
  (state: RootState['rooms']) => {
    const defaultTaxDisplayLogic = {
      includeLocalTaxes: defaultCountry.includeLocalTaxes,
      includeTaxes: defaultCountry.includeTaxes
    }

    return state?.taxDisplayLogic || defaultTaxDisplayLogic
  }
)
